import type {
	SRT_Row,
	SRT_RowSelectionState,
} from '@/shared/components/master-table'
import type { AnchorGeneratedFamilyModel } from '@/shared/generated'
import type { ExpandedState } from '@tanstack/react-table'
import type { TFunction } from 'i18next'
import type { AnchorApprovalTableItem } from '../../lib/types'

import { useMemo, useState } from 'react'
import {
	getCoreRowModel,
	getExpandedRowModel,
	getGroupedRowModel,
	getSortedRowModel,
} from '@tanstack/react-table'
import { Share2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import NumberField from '@/shared/components/data-grid/fields/Number/TableCell'
import PlainTextField from '@/shared/components/data-grid/fields/PlainText/TableCell'
import {
	createSRTColumnHelper,
	MasterTable,
	useMasterReactTable,
} from '@/shared/components/master-table'
import { useUpdateEffect } from '@/shared/hooks/useUpdateEffect'
import { getMasterTableLocale } from '@/shared/i18n'

import { useApprovalPageContext } from '../ApprovalPageProvider'
import { ApprovalTableFamilyGroup } from './ApprovalTableFamilyGroup'

export default function ApprovalTable() {
	const { t } = useTranslation()

	const [rowSelection, setRowSelection] = useState<SRT_RowSelectionState>({})
	const { data, isDataFetching } = useApprovalPageContext()
	const [expanded, setExpanded] = useState<ExpandedState>(true)

	useUpdateEffect(() => {
		// eslint-disable-next-line @eslint-react/set-state-in-effect
		setExpanded(true)
	}, [data])

	const columns = useMemo(() => getColumns(t), [t])

	const table = useMasterReactTable<AnchorApprovalTableItem>({
		localization: getMasterTableLocale(),
		data,
		columns,
		layoutMode: 'grid',
		divideY: true,
		state: {
			isLoading: isDataFetching,
			expanded,
			rowSelection,
			grouping: ['id'],
			columnVisibility: {
				'srt-row-expand': false,
				id: false,
			},
		},
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		divideX: true,
		enableVirtualization: true,
		defaultRowHeight: 36,
		getRowHeight: (row) => {
			if (!row) return 36
			if (row.depth === 1) {
				return 0
			}
			if (row?.groupingColumnId) {
				return 80
			}

			return 36
		},
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getGroupedRowModel: getGroupedRowModel(),
		onExpandedChange: setExpanded,
		onRowSelectionChange: setRowSelection,
		enableColumnActions: false,
		getSubRows: (row) =>
			(row as AnchorGeneratedFamilyModel)?.anchor_generated_records ??
			undefined,
		getRowClassName: ({ row }) => (row.depth === 1 ? 'hidden' : ''),
		renderGroup: ({ row }: { row: SRT_Row<AnchorApprovalTableItem> }) => (
			<ApprovalTableFamilyGroup row={row} />
		),
	})

	return <MasterTable table={table} />
}
const columnHelper = createSRTColumnHelper<AnchorApprovalTableItem>()
export const getColumns = (t: TFunction) => [
	columnHelper.accessor('id', {
		header: '',
		enableGrouping: true,
	}),
	columnHelper.accessor('sku', {
		header: t('sku'),
		Header: ({ column }) => (
			<span className='pl-9'>{column.columnDef.header}</span>
		),
		size: 300,
		Cell: ({ cellValue, row }) => {
			if ('sku' in row.original) {
				const anchorSku = row.getParentRow()?.original?.anchor_sku
				const isAnchor = cellValue === anchorSku

				return (
					<div className='flex items-center space-x-2 pl-9'>
						{isAnchor ? (
							<Share2Icon className='size-4' />
						) : (
							<div className='size-4' />
						)}
						<PlainTextField value={cellValue as string} />
					</div>
				)
			}
		},
	}),
	columnHelper.accessor('line_name', {
		header: t('product_name', { defaultValue: 'Product name' }),
		Cell: ({ cellValue }) => <PlainTextField value={cellValue as string} />,
	}),
	columnHelper.accessor('line_id', {
		header: t('app.pricing_line_id'),
		Cell: ({ cellValue }) => <PlainTextField value={cellValue as string} />,
		size: 110,
	}),
	columnHelper.accessor('anchor_mul', {
		header: t('mul_idx'),
		Cell: ({ cellValue }) => <NumberField value={cellValue as number} />,
		size: 84,
	}),
	columnHelper.accessor('anchor_add', {
		header: t('add_idx'),
		Cell: ({ cellValue }) => (
			<div className='pr-2'>
				<NumberField value={cellValue as number} />
			</div>
		),
		size: 84,
	}),
]
